import { Api } from 'src/api/zrm';
import type { RequestParams, TvDashboard, TvDevice } from 'src/api/zrm';
import type { TvDashboardSnapshot } from 'src/tv/dashboardTypes';
import httpStatusOf from 'src/utils/httpStatusOf';

/**
 * The wall screen's own client. It runs before the Auth0/provider stack (see `src/index.tsx`), so it
 * cannot use `useApi`; it talks to the unauthenticated `/tv/pair/*` endpoints and, once paired, sends
 * `X-TV-Token`.
 */

/** Same pick as `APIContext`: the Norwegian API on zensum.no hosts. */
export const tvApiBaseUrl = (hostname: string = window.location.hostname): string => (
  hostname.endsWith('zensum.no') ? import.meta.env.VITE_ZRM_URL_NO : import.meta.env.VITE_ZRM_URL
);

// A client of its own, without `securityWorker`: no Auth0 `Authorization` header, and no cookies. A failed
// response rejects with the `Response` (read its status with `httpStatusOf`); a network failure rejects
// with the fetch error, which has no status.
const tvApi = new Api({ baseUrl: tvApiBaseUrl(), baseApiParams: { credentials: 'omit' } });

// Storage only persists the token across reloads; `TvApp` holds the live copy in memory. It can be
// missing or throw (private mode, blocked site data): the screen then stays paired until reload.
const tokenKey = (dashboard: TvDashboard) => `zrm-tv-token:${dashboard}`;

export const readTvToken = (dashboard: TvDashboard): string | null => {
  try {
    return window.localStorage.getItem(tokenKey(dashboard));
  } catch {
    return null;
  }
};

export const storeTvToken = (dashboard: TvDashboard, token: string): void => {
  try {
    window.localStorage.setItem(tokenKey(dashboard), token);
  } catch {
    // Not persisted: `TvApp` still holds the token, so the screen works until reload, then pairs again.
  }
};

/**
 * Forgets `token`, but only while storage still holds it: a late answer for an old token must not remove the
 * token a new pairing has stored since.
 */
export const clearTvToken = (dashboard: TvDashboard, token: string): void => {
  try {
    if (window.localStorage.getItem(tokenKey(dashboard)) === token) window.localStorage.removeItem(tokenKey(dashboard));
  } catch {
    // Nothing stored that we could reach anyway.
  }
};

export const startTvPairing = async (dashboard: TvDashboard) => (await tvApi.tv.startTvPairingTvPairStartPost({ dashboard })).data;

export const pollTvPairing = async (pairingId: string, pollSecret: string) => (
  (await tvApi.tv.pollTvPairingTvPairPollPost({ pairing_id: pairingId, poll_secret: pollSecret })).data
);

/**
 * The server no longer knows the screen's token (revoked, or idle too long): the token is forgotten and
 * the screen pairs again. Thrown on a 401 only; the TV queries never retry it (`tvQueries.ts`).
 */
export class TvUnpairedError extends Error {
  constructor() {
    super('The TV token was rejected (401)');
    this.name = 'TvUnpairedError';
  }
}

/** Rethrows a 401 as `TvUnpairedError`, forgetting the token it was for; any other failure keeps it. */
const unpairedOn401 = (dashboard: TvDashboard, token: string, error: unknown): never => {
  if (httpStatusOf(error) === 401) {
    clearTvToken(dashboard, token);

    throw new TvUnpairedError();
  }

  throw error;
};

/**
 * Sends a token request that `signal` (TanStack's, aborted when the screen unmounts) can cancel. The generated
 * client ignores a `signal` in its params, so it goes through the client's own `cancelToken`.
 */
const tokenRequest = async <T>(token: string, signal: AbortSignal | undefined, send: (params: RequestParams) => Promise<T>): Promise<T> => {
  const cancelToken = Symbol('tv-request');
  const abort = () => tvApi.abortRequest(cancelToken);

  signal?.addEventListener('abort', abort, { once: true });

  try {
    return await send({ headers: { 'X-TV-Token': token }, cancelToken });
  } finally {
    signal?.removeEventListener('abort', abort);
    // The client keeps a request's controller after a failed fetch; drop it (a no-op once the answer is read).
    abort();
  }
};

/** The paired device. */
export const fetchTvDevice = async (dashboard: TvDashboard, token: string, signal?: AbortSignal): Promise<TvDevice> => {
  try {
    return (await tokenRequest(token, signal, (params) => tvApi.tv.getTvMeTvMeGet(params))).data;
  } catch (error) {
    return unpairedOn401(dashboard, token, error);
  }
};

/**
 * The screen's dashboard snapshot, or null when zrm has not built the first one for this screen yet
 * ("no data yet", still paired).
 */
export const fetchTvDashboard = async (
  dashboard: TvDashboard,
  token: string,
  signal?: AbortSignal,
): Promise<TvDashboardSnapshot | null> => {
  try {
    const { data } = await tokenRequest(token, signal, (params) => tvApi.tv.getTvDashboardTvDashboardGet(params));

    // The generated type marks zrm's defaulted counters and goals optional; the response carries them all.
    return data as TvDashboardSnapshot | null;
  } catch (error) {
    return unpairedOn401(dashboard, token, error);
  }
};

/** Retry delay after `failures` consecutive failures: 3 s doubling up to one minute, never faster. */
export const retryDelayMs = (failures: number): number => Math.min(60_000, 3_000 * 2 ** failures);
